/**
 * Step ownership and the gates the guided-section status drives
 * (SPEC-SOR-174 R10/R11/R21/R23/R30).
 *
 * The two modules below this one answer "what IS this section's state" —
 * `guided-sections.ts` the status, `section-readiness.ts` the readiness. This
 * one is the only place that turns those answers into a REFUSAL: which step
 * owns which section (AX3), whether a step's Next may enable, whether Submit
 * may enable, and whether the shared order validator reports the order valid.
 *
 * Two disciplines are load-bearing here and are structural rather than
 * conventional:
 *
 * 1. **R23 — the guided status is the sole gating authority.** Every predicate
 *    takes `HasSectionStatuses`, a slice carrying `sectionStatuses` and nothing
 *    else, so the legacy per-card record `ocrConfirmedFields` is not merely
 *    unread: it is not in scope to read. The legacy field keeps its writer and
 *    its persistence; what it loses is any say in a gate.
 *
 * 2. **R21 — no deadlock.** A step's blockers are drawn from `STEP_SECTIONS`
 *    for that step alone, so the Upload step cannot name the parties or
 *    documents section however the wizard calls in. The rule holds by
 *    construction rather than by every call site remembering it (WOL-419).
 *
 * Like its two siblings this module is dependency-free with respect to
 * `../types.js` — `Step` is spelled out here rather than imported from the tab
 * union, because a step is not a tab: `GuidedStep` names the two steps that own
 * sections, while `EFilingTab` also carries the classic tabs and the fee and
 * submit steps, which own none. The `.js` extensions are the Docker-build ESM
 * rule, not an optional style.
 */
import { getSectionStatus, SECTION_ORDER, } from './guided-sections.js';
import { confirmable, partiesReferenceExtractionEntry, } from './section-readiness.js';
import { caseCourtBinding } from '../common/court-identity.js';
/**
 * AX3 — the ownership map, upload half. Document TYPING stays on Upload as the
 * foundation it always was; what Upload owns as a confirmation unit is the
 * court and the case.
 */
export const UPLOAD_SECTIONS = ['sec_court', 'sec_case'];
/**
 * AX3 — the ownership map, confirm half. The documents section is confirmed
 * HERE, not on Upload, because what a filer approves about the documents is
 * their party assignments, and those live on the Confirm step.
 */
export const CONFIRM_SECTIONS = [
    'sec_parties',
    'sec_documents',
];
/**
 * AX3 in one value — `owner_step` inverted. Every member of `SECTION_ORDER`
 * appears exactly once across the two halves; `sectionOwnershipTotal` below is
 * the check, and the R21 no-deadlock argument depends on it (a section owned by
 * no step could never be named, one owned by both could be named from Upload).
 */
export const STEP_SECTIONS = {
    upload: UPLOAD_SECTIONS,
    confirm: CONFIRM_SECTIONS,
};
/** Plain-language name of each section, as the filer reads it on screen. */
export const SECTION_LABEL = {
    sec_court: 'Court',
    sec_case: 'Case',
    sec_parties: 'Parties',
    sec_documents: 'Documents',
};
/**
 * The `validateTab` rule id each section's unconfirmed blocker carries. Keyed
 * by section rather than assembled at the call site so the wizard's blocker
 * presentation map and the validator cannot drift apart.
 */
export const SECTION_GATE_RULE = {
    sec_court: 'upload-court-section-required',
    sec_case: 'upload-case-section-required',
    sec_parties: 'confirm-parties-section-required',
    sec_documents: 'confirm-documents-section-required',
};
/** The `ValidationError.field` each section's blocker points at. */
export const SECTION_GATE_FIELD = {
    sec_court: 'sectionStatuses.sec_court',
    sec_case: 'sectionStatuses.sec_case',
    sec_parties: 'sectionStatuses.sec_parties',
    sec_documents: 'sectionStatuses.sec_documents',
};
/** The order-level rule R30 raises when any guided section is unconfirmed. */
export const SECTIONS_UNCONFIRMED_RULE = 'sections-unconfirmed';
/** `owner_step` — the step that owns a section, or `undefined` for a non-section. */
export function ownerStep(section) {
    if (UPLOAD_SECTIONS.includes(section))
        return 'upload';
    if (CONFIRM_SECTIONS.includes(section))
        return 'confirm';
    return undefined;
}
/**
 * True when every ordered section is owned by exactly one step. A CHECK on the
 * map, never a repair — a false here means AX3 was edited into a shape the R21
 * no-deadlock argument no longer covers, and the fix is the map, not a fallback.
 */
export function sectionOwnershipTotal() {
    return SECTION_ORDER.every((section) => Number(UPLOAD_SECTIONS.includes(section)) + Number(CONFIRM_SECTIONS.includes(section)) === 1);
}
/**
 * The sections a step owns that are not confirmed, in confirmation order.
 *
 * This is the ONLY source of a step's section blockers, which is what makes R21
 * structural: `STEP_SECTIONS.upload` holds no parties or documents entry, so no
 * argument produces an Upload blocker naming one.
 */
export function unconfirmedSectionsForStep(state, step) {
    return SECTION_ORDER.filter((section) => STEP_SECTIONS[step].includes(section) && getSectionStatus(state, section) !== 'confirmed');
}
/** R10's antecedent — the step owns at least one section the filer has not confirmed. */
export function stepHasUnconfirmedSection(state, step) {
    return unconfirmedSectionsForStep(state, step).length > 0;
}
/**
 * R11 + R30's antecedent — some guided section is not confirmed.
 *
 * Quantified over `SECTION_ORDER`, not over the persisted map's keys: an absent
 * entry reads `pending`, so a draft carrying no map at all — the fully-ready,
 * zero-confirmation draft W12 names — is unconfirmed in all four sections
 * rather than vacuously confirmed in none.
 */
export function anyGuidedSectionUnconfirmed(state) {
    return SECTION_ORDER.some((section) => getSectionStatus(state, section) !== 'confirmed');
}
/**
 * R10's consequent, message half — the section and its unmet requirement in
 * plain language.
 *
 * Two readings, because an unconfirmed section is unconfirmed for one of two
 * different reasons and telling the filer the wrong one is a dead stall: a
 * section that is READY needs only the filer's approval, while a section that
 * is not ready names the thing standing in the way of that approval. Every
 * not-ready message points at a control on the step that owns the section, so
 * the WOL-419 satisfiable-in-place rule survives the wording.
 */
export function sectionGateMessage(state, section, options) {
    const label = SECTION_LABEL[section];
    if (confirmable(state, section, options)) {
        return `Review the ${label} section and choose Confirm to continue.`;
    }
    switch (section) {
        case 'sec_court':
            return 'Select the filing state and county in the Court section, then confirm it.';
        case 'sec_case':
            if (state.orderInfo.filingType === 'subsequent-filing') {
                // Three distinguishable faults, and they are NOT interchangeable. A
                // case IS attached in the latter two, so "attach the court case" sends
                // that filer back to redo the one step they already completed while
                // saying nothing about what is actually wrong — which court the
                // attached case belongs to. Reached only when the section is not
                // confirmable, so by `caseSectionConfirmable` an attached case here
                // binds `foreign-court` or `absent`; the trailing return is the
                // unreachable-by-construction default, not a fourth case.
                //
                // The two binding wordings deliberately do NOT restate the
                // `case-county-mismatch` / `case-binding-absent` rules. Those rules fire
                // on this same draft and land in the same blocker panel, so a message
                // that repeated their sentence would render the fault twice in a row —
                // and an assertion for either rule's wording would then match two
                // elements. Each says the same thing about the same fault from this
                // section's angle: what the SECTION needs before it can be confirmed,
                // pointing at the lookup on the step that owns it (WOL-419).
                if (!state.caseInfo.caseTrackingId) {
                    return 'Attach the court case in the Case section, then confirm it.';
                }
                const binding = caseCourtBinding(state.caseInfo, state.orderInfo.county);
                if (binding === 'foreign-court') {
                    return 'The attached case is from another county, so the Case section cannot be confirmed. Attach a case from the current county — or change the filing county back to that court.';
                }
                if (binding === 'absent') {
                    return 'The attached case has no recorded court. Search for it again in the Case section to re-attach it, then confirm it.';
                }
                return 'Attach the court case in the Case section, then confirm it.';
            }
            if (state.orderInfo.filingType === 'case-initiation') {
                return 'Select the case category and type in the Case section, then confirm it.';
            }
            return 'Choose whether this is a new or existing case in the Case section, then confirm it.';
        case 'sec_parties':
            // Two distinguishable faults, and naming the wrong one is a dead stall.
            // A subsequent filing whose documents point at a participant the court
            // record does not have is over-supplied, not under-supplied: telling that
            // filer to "complete" the section sends them looking for a missing entry
            // they cannot find. Both messages point at the same step's controls.
            if (state.orderInfo.filingType === 'subsequent-filing' &&
                partiesReferenceExtractionEntry(state)) {
                return "A document is filed on behalf of someone who is not on the court's record for this case. Re-pick that participant from the case roster in the Parties section, then confirm it.";
            }
            return 'Complete the Parties section — every party and attorney the filing needs — then confirm it.';
        case 'sec_documents':
            return 'Complete the Documents section — each document needs a type and its party assignments — then confirm it.';
        default:
            return `Confirm the ${label} section to continue.`;
    }
}
